import React from 'react';
import { useTranslation } from 'react-i18next';

import { concat, findIndex, maxBy, sortBy, values } from 'lodash';
import { Flex, Tooltip } from 'antd';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faArrowRight,
	faEquals,
	faTrophy
} from '@fortawesome/pro-light-svg-icons';

import './ScenarioResult.sass';

import {
	getScoreColor,
	scenarioScore,
	getStakeholderScoreForScenarioDetailsColor,
	vulnerability,
	getRiskScore,
	getIndividualRiskScore,
	getOpportunityScore,
	singleHue,
} from 'utils/ranking';
import HorizontalVulnerabilityScale from 'components/Radar/HorizontalVulnerabilityScale';
import {
	getItemAlteredByScenario,
	vulnerabilityIndicators,
	vulnerabilityStakes
} from 'utils/rules';
import Nav from './Nav';

interface ScenarioResultProps {
	scenarios?: any[];
	scenario?: any;
	stakeholders?: any[];
	indicators?: any[];
	stakes?: any[];
	risks?: any[];
	likelihoods?: any[];
	impacts?: any[];
	riskCategories?: any[];
	features?: any;
	onGoToPreviousScenario?: () => void;
	onGoToNextScenario?: () => void;
}

export const ScenarioResult = (props: ScenarioResultProps) => {

	const { t } = useTranslation();
	const { stakeholders = [], indicators = [], stakes = [], risks = [], likelihoods = [], impacts = [], scenarios = [] } = props;

	// Filtrer les éléments par enabled !== false après application du scénario courant
	const filteredStakeholders = stakeholders.filter((s: any) => 
		getItemAlteredByScenario(s, props.scenario).enabled !== false
	);
	const filteredStakes = stakes.filter((i: any) =>
		getItemAlteredByScenario(i, props.scenario).enabled !== false
	);
	const filteredIndicators = indicators.filter((c: any) => 
		getItemAlteredByScenario(c, props.scenario).enabled !== false
	);
	const filteredRisks = risks.filter((r: any) => 
		getItemAlteredByScenario(r, props.scenario).enabled !== false
	);

	const scenarioIndex = findIndex(props.scenarios, { id: props.scenario?.id }) + 1;

	if (!props.scenarios) {
		return (
			<div className="ScenarioResult">
				<h2>{t('models.scenario.no_x')}</h2>
			</div>
		);
	}

	let stakeholderScore = filteredStakeholders && filteredStakeholders.length > 0 ?
		100 -
		vulnerability(
			filteredStakeholders.map((s: any) =>
				getItemAlteredByScenario(s, props.scenario)
			)
		) : null;
	let constraintsScore = vulnerabilityIndicators(
		filteredIndicators.map((c: any) =>
			getItemAlteredByScenario(c, props.scenario)
		)
	);
	let stakesScore = vulnerabilityStakes(
		filteredStakes.map((i: any) =>
			getItemAlteredByScenario(i, props.scenario)
		)
	);

	let maxStakeholderScores: any = {}
	let minStakeholderScores: any = {}
	let maxIndicatorsScores: any = {}
	let minIndicatorsScores: any = {}
	let maxRisksScores: any = {}
	let minRisksScores: any = {}
	let maxConstraintsScores: any = {}
	let minConstraintsScores: any = {}
	let maxOpportunitiesScores: any = {}
	let minOpportunitiesScores: any = {}

	let stakeholdersScores: any = {}
	let stakesScores: any = {}
	let risksScores: any = {}
	let opportunitiesScores: any = {}
	let constraintsScores: any = {}



	const maxLikelihood = likelihoods && parseInt(maxBy(values(likelihoods), 'id')?.id || 3);
	const maxImpact = impacts && parseInt(maxBy(values(impacts), 'id')?.id || 3);

	concat(scenarios || [], {}).forEach((sc: any) => {

		console.log({
			sc
		})

		filteredStakeholders.forEach((s: any) => {

			if (!stakeholdersScores[s.id]) {
				stakeholdersScores[s.id] = {}
			}

			stakeholdersScores[s.id][sc.id] = getStakeholderScoreForScenarioDetailsColor(getItemAlteredByScenario(s, sc))

			stakeholdersScores[s.id][sc.id || "baseline"] = getStakeholderScoreForScenarioDetailsColor(getItemAlteredByScenario(s, sc))
			if (!maxStakeholderScores[s.id] || getStakeholderScoreForScenarioDetailsColor(getItemAlteredByScenario(s, sc)) > maxStakeholderScores[s.id]) {
				maxStakeholderScores[s.id] = getStakeholderScoreForScenarioDetailsColor(getItemAlteredByScenario(s, sc))
			}
			if (!minStakeholderScores[s.id] || getStakeholderScoreForScenarioDetailsColor(getItemAlteredByScenario(s, sc)) < minStakeholderScores[s.id]) {
				minStakeholderScores[s.id] = getStakeholderScoreForScenarioDetailsColor(getItemAlteredByScenario(s, sc))
			}
		})

		filteredStakes.forEach((i: any) => {
			if (!stakesScores[i.id]) {
				stakesScores[i.id] = {}
			}

			stakesScores[i.id][sc.id || "baseline"] = parseFloat(getItemAlteredByScenario(i, sc).trend)

			if (!maxIndicatorsScores[i.id] || parseFloat(getItemAlteredByScenario(i, sc).trend) > maxIndicatorsScores[i.id]) {
				maxIndicatorsScores[i.id] = parseFloat(getItemAlteredByScenario(i, sc).trend)
			}
			if (!minIndicatorsScores[i.id] || parseFloat(getItemAlteredByScenario(i, sc).trend) < minIndicatorsScores[i.id]) {
				minIndicatorsScores[i.id] = parseFloat(getItemAlteredByScenario(i, sc).trend)
			}
		})

		filteredRisks.filter((r: any) => !r.isOpportunity).forEach((r: any) => {

			if (!risksScores[r.id]) {
				risksScores[r.id] = {}
			}

			const riskScore = getIndividualRiskScore(getItemAlteredByScenario(r, sc))

			risksScores[r.id][sc.id || "baseline"] = riskScore

			if (!maxRisksScores[r.id] || riskScore < maxRisksScores[r.id]) {
				maxRisksScores[r.id] = riskScore
			}
			if (!minRisksScores[r.id] || riskScore > minRisksScores[r.id]) {
				minRisksScores[r.id] = riskScore
			}
		})

		filteredRisks.filter((r: any) => r.isOpportunity).forEach((r: any) => {

			if (!opportunitiesScores[r.id]) {
				opportunitiesScores[r.id] = {}
			}

			const opportunityScore = getOpportunityScore([getItemAlteredByScenario(r, sc)], maxLikelihood, maxImpact, props.riskCategories) || 0

			opportunitiesScores[r.id][sc.id || "baseline"] = opportunityScore

			if (!maxOpportunitiesScores[r.id] || opportunityScore > maxOpportunitiesScores[r.id]) {
				maxOpportunitiesScores[r.id] = opportunityScore
			}
			if (!minOpportunitiesScores[r.id] || opportunityScore < minOpportunitiesScores[r.id]) {
				minOpportunitiesScores[r.id] = opportunityScore
			}
		})

		filteredIndicators.forEach((c: any) => {

			if (!constraintsScores[c.id]) {
				constraintsScores[c.id] = {}
			}

			constraintsScores[c.id][sc.id || "baseline"] = parseFloat(getItemAlteredByScenario(c, sc).trend)

			if (!maxConstraintsScores[c.id] || parseFloat(getItemAlteredByScenario(c, sc).trend) > maxConstraintsScores[c.id]) {
				maxConstraintsScores[c.id] = parseFloat(getItemAlteredByScenario(c, sc).trend)
			}
			if (!minConstraintsScores[c.id] || parseFloat(getItemAlteredByScenario(c, sc).trend) < minConstraintsScores[c.id]) {
				minConstraintsScores[c.id] = parseFloat(getItemAlteredByScenario(c, sc).trend)
			}
		})
	});

	console.log({
		stakeholdersScores,
		stakesScores,
		risksScores,
		opportunitiesScores,
		constraintsScores
	})


	const allRisks = filteredRisks.filter((r: any) => !r.isOpportunity);
	const allOpportunities = filteredRisks.filter((r: any) => r.isOpportunity);

	let risksScore = getRiskScore(
		allRisks.map((i: any) =>
			getItemAlteredByScenario(i, props.scenario)
		),
		maxLikelihood,
		maxImpact,
		props.riskCategories
	);

	let opportunitiesScore = getOpportunityScore(
		allOpportunities.map((i: any) =>
			getItemAlteredByScenario(i, props.scenario)
		),
		maxLikelihood,
		maxImpact,
		props.riskCategories
	);

	let vulnerabilities = (props.scenarios || []).map((sc: any) => {
		return {
			id: sc.id,
			name: sc.name,
			vulnerability: scenarioScore(props, sc),
		};
	});

	vulnerabilities.push({
		id: null,
		vulnerability: scenarioScore(props, null)
	});

	return (
		<div className="ScenarioResult">
			<Nav
				className="mb-1"
				fullWidth
				count={props.scenarios.length}
				options={props.scenarios}
				onGoToPreviousScenario={props.onGoToPreviousScenario}
				onGoToNextScenario={props.onGoToNextScenario}

			>
				<Flex align="center" justify="center">
					<h2 title={props.scenarios ? props.scenario?.id : ''} className="mb-0">
						{props.scenario?.name || t("models.scenario.baseline")}{' '}
					</h2>

					{scenarioScore(props, props.scenario) !== null ? `
						(${Math.round(scenarioScore(props, props.scenario))} max 100)
					` : null}
				</Flex>

			</Nav>

			<HorizontalVulnerabilityScale
				scenarios={props.scenarios}
				scenario={props.scenario}
				vulnerabilities={vulnerabilities}
			></HorizontalVulnerabilityScale>

			<Flex align="top" justify="center" className="ElementsRank mt-4">
				{props.features?.indicators &&
					<div data-type="indicators" className="RankCriteriaColumn">
						<div className="title">
							{t('models.indicator.name_plural')}{' '}
							{constraintsScore !== undefined &&
								constraintsScore !== null && (
									<Tooltip title="Score">
										<div className="ml-auto">
											<small>
												({constraintsScore.toFixed(0)})
											</small>
										</div>
									</Tooltip>
								)}
						</div>
					{sortBy(
						filteredIndicators,
						(s: any) =>
							-constraintsScores[s.id][props.scenario?.id || "baseline"]
					).map((s: any) => {
							let baselineScore = constraintsScores[s.id]["baseline"];
							let sScore = constraintsScores[s.id][props.scenario?.id || "baseline"];

							return (
								<div className="RankCriteriaWrapper">
									<div
										className="RankCriteria"
										style={{
											borderLeftWidth: '8px',
											borderLeftStyle: 'solid',
											borderLeftColor: getScoreColor(
												getItemAlteredByScenario(
													s,
													props.scenario
												).trend
											)
										}}
									>
										{props.scenario &&
											sScore < baselineScore && (
												<div className={'rp45'}>
													<Tooltip title="Worst than baseline">
														<FontAwesomeIcon
															icon={faArrowRight}
															size="lg"
														/>
													</Tooltip>
												</div>
											)}
										{props.scenario &&
											sScore > baselineScore && (
												<div className={'rm45'}>
													<Tooltip title="Better than baseline">
														<FontAwesomeIcon
															icon={faArrowRight}
															size="lg"
														/>
													</Tooltip>
												</div>
											)}
										{props.scenario &&
											sScore == baselineScore && (
												<div className={'r0'}>
													<Tooltip title="Same as baseline">
														<FontAwesomeIcon
															icon={faEquals}
															size="lg"
														/>
													</Tooltip>
												</div>
											)}
										{
											// If this indicator scenario score is the best across all scenarios
											sScore === maxConstraintsScores[s.id] && sScore !== minConstraintsScores[s.id]
											&&
											<Tooltip title={t("models.scenario.messages.best_evaluation")}>
												<FontAwesomeIcon className="trophy mr-05 ml-05" icon={faTrophy} />
											</Tooltip>}
										{s.name}
									</div>
								</div>
							);
						})}
					</div>
				}
				{props.features?.stakes &&
					<div data-type="stakes" className="RankCriteriaColumn">
						<div className="title">
							{t('models.stake.name_plural')}{' '}
							{stakesScore !== undefined &&
								stakesScore !== null && (
									<Tooltip title="Score">
										<div className="ml-auto">
											<small>
												({stakesScore.toFixed(0)})
											</small>
										</div>
									</Tooltip>
								)}
						</div>

					{sortBy(
						filteredStakes,
						(s: any) =>
							-getItemAlteredByScenario(s, props.scenario).trend
					).map((s: any) => {
							let baselineScore = stakesScores[s.id]["baseline"];
							let sScore = stakesScores[s.id][props.scenario?.id || "baseline"];
							return (
								<div className="RankCriteriaWrapper">
									<div
										className="RankCriteria"
										style={{
											borderLeftWidth: '8px',
											borderLeftStyle: 'solid',
											borderLeftColor: getScoreColor(
												getItemAlteredByScenario(
													s,
													props.scenario
												).trend
											)
										}}
									>
										{props.scenario &&
											sScore < baselineScore && (
												<div className={'rp45'}>
													<Tooltip title="Worst than baseline">
														<FontAwesomeIcon
															icon={faArrowRight}
															size="lg"
														/>
													</Tooltip>
												</div>
											)}
										{props.scenario &&
											sScore > baselineScore && (
												<div className={'rm45'}>
													<Tooltip title="Better than baseline">
														<FontAwesomeIcon
															icon={faArrowRight}
															size="lg"
														/>
													</Tooltip>
												</div>
											)}
										{props.scenario &&
											sScore == baselineScore && (
												<div className={'r0'}>
													<Tooltip title="Same as baseline">
														<FontAwesomeIcon
															icon={faEquals}
															size="lg"
														/>
													</Tooltip>
												</div>
											)}

										{
											// If this indicator scenario score is the best across all scenarios
											sScore === maxIndicatorsScores[s.id] && sScore !== minIndicatorsScores[s.id]
											&&
											<Tooltip title={t("models.scenario.messages.best_evaluation")}>
												<FontAwesomeIcon className="trophy mr-05 ml-05" icon={faTrophy} />
											</Tooltip>}
										{s.name}
									</div>
								</div>
							);
						})}
					</div>
				}

				<div data-type="stakeholders" className="RankCriteriaColumn">
					<div className="title">
						{t('models.item.name_plural')}{' '}
						{stakeholderScore !== undefined &&
							stakeholderScore !== null && (
								<Tooltip title="Score">
									<div className="ml-auto">
										<small>
											({stakeholderScore.toFixed(0)})
										</small>
									</div>
								</Tooltip>
							)}
					</div>
					{sortBy(
						filteredStakeholders,
						(s: any) =>
							-stakeholdersScores[s.id][props.scenario?.id || "baseline"]
					).map((s: any) => {
						let baselineScore = stakeholdersScores[s.id]["baseline"];
						let sScore = stakeholdersScores[s.id][props.scenario?.id || "baseline"];

						return (
							<div className="RankCriteriaWrapper">
								<div
									className={`RankCriteria`}
									style={{
										borderLeftWidth: '8px',
										borderLeftStyle: 'solid',
										borderLeftColor: getScoreColor(
											sScore
										)
									}}
								>
									{props.scenario &&
										sScore < baselineScore && (
											<div className={'rp45'}>
												<Tooltip title="Worst than baseline">
													<FontAwesomeIcon
														icon={faArrowRight}
														size="lg"
													/>
												</Tooltip>
											</div>
										)}
									{props.scenario &&
										sScore > baselineScore && (
											<div className={'rm45'}>
												<Tooltip title="Better than baseline">
													<FontAwesomeIcon
														icon={faArrowRight}
														size="lg"
													/>
												</Tooltip>
											</div>
										)}
									{props.scenario &&
										sScore == baselineScore && (
											<div className={'r0'}>
												<Tooltip title="Same as baseline">
													<FontAwesomeIcon
														icon={faEquals}
														size="lg"
													/>
												</Tooltip>
											</div>
										)}

									{
										// If this stakeholder scenario score is the best across all scenarios
										sScore === maxStakeholderScores[s.id] && sScore !== minStakeholderScores[s.id]
										&&
										<Tooltip title={t("models.scenario.messages.best_evaluation")}>
											<FontAwesomeIcon className="trophy mr-05 ml-05" icon={faTrophy} />
										</Tooltip>}

									{s.name}{' '}
								</div>
							</div>
						);
					})}
				</div>
				{
					props.features?.risks && <div data-type="risks" className="RankCriteriaColumn">
						<div className="title">
							{t('models.risk.name_plural')}{' '}
							{risksScore !== undefined &&
								risksScore !== null && (
									<Tooltip title="Score">
										<div className="ml-auto">
											<small>
												({risksScore.toFixed(0)})
											</small>
										</div>
									</Tooltip>
								)}
						</div>
						{sortBy(
							allRisks,
							(s: any) =>
								risksScores[s.id][props.scenario?.id || "baseline"]
						).map((s: any) => {
							let baselineScore = risksScores[s.id]["baseline"];
							let sScore = risksScores[s.id][props.scenario?.id || "baseline"];
							let colorScore = (getRiskScore([getItemAlteredByScenario(s, props.scenario)], maxLikelihood, maxImpact, props.riskCategories))

							return (
								<div className="RankCriteriaWrapper">
									<div
										className="RankCriteria"
										style={{
											borderLeftWidth: '8px',
											borderLeftStyle: 'solid',
											borderLeftColor: singleHue(colorScore, false)
										}}
									>

										{props.scenario &&
											sScore > baselineScore && (
												<div className={'rp45'}>
													<Tooltip title="Worst than baseline">
														<FontAwesomeIcon
															icon={faArrowRight}
															size="lg"
														/>
													</Tooltip>
												</div>
											)}
										{props.scenario &&
											sScore < baselineScore && (
												<div className={'rm45'}>
													<Tooltip title="Better than baseline">
														<FontAwesomeIcon
															icon={faArrowRight}
															size="lg"
														/>
													</Tooltip>
												</div>
											)}
										{props.scenario &&
											sScore == baselineScore && (
												<div className={'r0'}>
													<Tooltip title="Same as baseline">
														<FontAwesomeIcon
															icon={faEquals}
															size="lg"
														/>
													</Tooltip>
												</div>
											)}

										{
											// If this risk scenario score is the best across all scenarios
											sScore === maxRisksScores[s.id] && sScore !== minRisksScores[s.id]
											&&
											<Tooltip title={t("models.scenario.messages.best_evaluation")}>
												<FontAwesomeIcon className="trophy mr-05 ml-05" icon={faTrophy} />
											</Tooltip>}

										{s.name}{' '}
									</div>
								</div>
							);
						})}
					</div>
				}
				{
					props.features?.risks && <div data-type="opportunities" className="RankCriteriaColumn">
						<div className="title">
							{t('models.risk.name_plural_positive')}{' '}
							{opportunitiesScore !== undefined &&
								opportunitiesScore !== null && (
									<Tooltip title="Score">
										<div className="ml-auto">
											<small>
												({opportunitiesScore.toFixed(0)})
											</small>
										</div>
									</Tooltip>
								)}
						</div>
						{sortBy(
							allOpportunities,
							(s: any) =>
								-opportunitiesScores[s.id][props.scenario?.id || "baseline"]
						).map((s: any) => {
							let baselineScore = opportunitiesScores[s.id]["baseline"];
							let sScore = opportunitiesScores[s.id][props.scenario?.id || "baseline"];
							let colorScore = (getOpportunityScore([getItemAlteredByScenario(s, props.scenario)], maxLikelihood, maxImpact, props.riskCategories))

							return (
								<div className="RankCriteriaWrapper">
									<div
										className="RankCriteria"
										style={{
											borderLeftWidth: '8px',
											borderLeftStyle: 'solid',
											borderLeftColor: singleHue(colorScore, true)
										}}
									>
										{props.scenario &&
											sScore < baselineScore && (
												<div className={'rp45'}>
													<Tooltip title="Worst than baseline">
														<FontAwesomeIcon
															icon={faArrowRight}
															size="lg"
														/>
													</Tooltip>
												</div>
											)}
										{props.scenario &&
											sScore > baselineScore && (
												<div className={'rm45'}>
													<Tooltip title="Better than baseline">
														<FontAwesomeIcon
															icon={faArrowRight}
															size="lg"
														/>
													</Tooltip>
												</div>
											)}
										{props.scenario &&
											sScore == baselineScore && (
												<div className={'r0'}>
													<Tooltip title="Same as baseline">
														<FontAwesomeIcon
															icon={faEquals}
															size="lg"
														/>
													</Tooltip>
												</div>
											)}

										{
											// If this opportunity scenario score is the best across all scenarios
											sScore === maxOpportunitiesScores[s.id] && sScore !== minOpportunitiesScores[s.id]
											&&
											<Tooltip title={t("models.scenario.messages.best_evaluation")}>
												<FontAwesomeIcon className="trophy mr-05 ml-05" icon={faTrophy} />
											</Tooltip>}

										{s.name}{' '}
									</div>
								</div>
							);
						})}
					</div>
				}
			</Flex>

			<Nav
				fullWidth
				count={props.scenarios.length}
				options={props.scenarios}
				onGoToPreviousScenario={props.onGoToPreviousScenario}
				onGoToNextScenario={props.onGoToNextScenario}
			>
				<Flex align="center" justify="center" className="h-100">
					{scenarioIndex + 1} / {props.scenarios.length + 1}
				</Flex>
			</Nav>
		</div >
	);
};